import { z } from 'zod';
import { MAX_APPRAISAL_BONUS, MAX_APPRAISAL_SCORE, MIN_APPRAISAL_BONUS, MIN_APPRAISAL_SCORE, } from '../constants/index.js';
import { archetypeIdSchema, characterStatsSchema, elementIdSchema, potentialPointsSchema, traitIdSchema, } from './character.js';
const appraisalScoreSchema = z.number().int().min(MIN_APPRAISAL_SCORE).max(MAX_APPRAISAL_SCORE);
/** AI が付けた 6 項目の数値（1〜10）。素質ポイント・鑑定ボーナス・基礎の能力値の比がここから決まる。 */
export const appraisalScoresSchema = z.object({
    vitality: appraisalScoreSchema,
    power: appraisalScoreSchema,
    defense: appraisalScoreSchema,
    speed: appraisalScoreSchema,
    magic: appraisalScoreSchema,
    uniqueness: appraisalScoreSchema,
});
/**
 * AI 鑑定の結果（`POST /api/v1/characters/analyze` のレスポンス）。
 *
 * 数値や系統は**サーバー側ルールで確定した値**（企画書 §7.2）。AI の出力そのものではない。
 * 白紙・文字だけの絵（`DRAWING_NOT_RECOGNIZED`）と問題のある絵（`DRAWING_INAPPROPRIATE`）は
 * 鑑定を断るので、この形では返らない。
 *
 * iOS が同じ形を `Codable` で読むので、契約テスト（`packages/contracts/mobile/`）がこのスキーマで
 * サーバーの出力を縛る（ADR-015）。
 */
export const appraisalSchema = z.object({
    id: z.string().min(1),
    /** R2 に保存した鑑定元の絵。 */
    imageUrl: z.string().url(),
    /** 総合素質ポイント（50〜100）。`scores` の合計から決める。`baseStats` の合計 + `bonusPoints`。 */
    potentialPoints: potentialPointsSchema,
    /**
     * 基礎の能力値。**ステ振りでこれより下げられない**（ADR-019）。絵の特徴を数値に残すため。
     */
    baseStats: characterStatsSchema,
    /** 鑑定ボーナス（0〜30）。プレイヤーが基礎の能力値に自由に足せるポイント。 */
    bonusPoints: z.number().int().min(MIN_APPRAISAL_BONUS).max(MAX_APPRAISAL_BONUS),
    archetypeId: archetypeIdSchema,
    /** 表現が無ければ `none`（相性なし）。バトルでは相性に使う（ADR-027）。 */
    elementId: elementIdSchema,
    /** 0〜1 個。合う特性が無ければ付けない。 */
    traitIds: z.array(traitIdSchema).max(1),
    scores: appraisalScoresSchema,
    /** 何が描かれているか（例: ほのおをはくドラゴン）。 */
    subject: z.string().min(1),
    /** 子ども向けの鑑定コメント。 */
    comment: z.string().min(1),
    /**
     * 必殺技の名前の案（#204、ADR-028）。アプリが名前とステ振りの画面の入力欄に入れる。
     * **使えなかったとき（空・長すぎ・見せられない文字）と、この版より前の鑑定では省く。**
     */
    specialMoveName: z.string().min(1).optional(),
    createdAt: z.string(),
});
